/**
 * Auto-seat: PROPOSE a seating for a chart. Never applies anything.
 *
 * `planAutoSeat` is a pure function of what the host's screen already holds — the
 * chart's tables and chairs, its candidates, the bonds between guests, their
 * household / relationship tags, and a SEED — and returns a plan the host reads
 * before anything is written. The plan is then turned into the ONE request the
 * server already has for a whole-room change (`POST .../seats/batch`, see
 * {@link toSeatBatch}), so what was previewed is byte-for-byte what is applied.
 * Nothing here talks to the network, reads a clock, or calls `Math.random`.
 *
 * Rules, in the order they win (the placement itself is lib/autoSeatEngine.ts,
 * the seat pattern lib/autoSeatSeats.ts):
 *
 *  1. HARD RULES, never bent by anything below:
 *     - never exceed a table's capacity;
 *     - a party (a guest and their plus-one chairs) is never split, and guests
 *       joined by a 'must' keep-together link are one party for this purpose;
 *     - 'must' keep-apart is a wall: nobody sits at a table holding someone they
 *       must be kept apart from;
 *     - fill mode never moves an existing chair. Re-seat mode re-plans only the
 *       people the plan itself covers: a blocked chair, or a chair held by
 *       someone this plan is not seating, is never touched;
 *     - a LOCKED guest is exactly where the host pinned them, in either mode.
 *  2. HOUSEHOLD tags: members sit at one table when any table can hold them;
 *     otherwise the fewest adjacent tables, with a warning.
 *  3. RELATIONSHIP groups (the "group" tag kind): each sits together as a block,
 *     a group bigger than a table spills into ADJACENT tables, small groups pack
 *     into a table together. A guest with several group tags uses the first in
 *     the host's tag list. "Mix groups" instead spreads each group round-robin.
 *  4. BALANCE: tables end up as evenly filled as the groups allow; spare seats
 *     sit at the end of a table, spare tables at the end of the room.
 *  5. UNTAGGED guests fill what is left, near a prefer-link partner if they have
 *     one, otherwise at a seeded-random table.
 *  6. SEAT PATTERN for pairs (side by side / across / one seat apart / anywhere),
 *     honoured within a table from the table's real geometry (lib/seatRelation.ts).
 *  7. RANDOMNESS: every ordering decision the rules above do not fix comes from
 *     the seeded PRNG in lib/seededRandom.ts. NOTHING is ordered by name; ids are
 *     used only to give the input one canonical order before it is shuffled, so
 *     the order guests arrived in cannot leak. Same seed + same input = same plan.
 *  8. Whoever cannot be seated is REPORTED with a reason, never silently dropped.
 *
 * Who is seated: on a dinner-bound chart, the dinner's claimants (the server has
 * already filtered candidates to capacity-reducing claims). On a roster chart,
 * the guests who said they are going — the same definition of "attending" the
 * progress panel uses — plus, only if the host asks, everyone who has not said no.
 *
 * Deliberately NOT here: dietary / access / logistics tags do not influence
 * seating, and there is no head-table or VIP notion. See the open decisions in
 * the PR.
 */
import { placeUnits, type SeatGrouping } from './autoSeatEngine';
import type {
  BondIndex,
  ChairSpec,
  LockedChair,
  Party,
  PlannedChair,
  UnseatedGuest,
  Unit,
} from './autoSeatTypes';
import { normalizeName } from './invitee-match';
import type { SeatPattern } from './seatRelation';
import type { SeatBond } from './seatingBonds';
import { seatMapFor } from './seatingCandidates';
import { groupLabels, type GuestGroups } from './seatingGroups';
import type {
  SeatAssignment,
  SeatBatchRequest,
  SeatingCandidate,
  SeatingTable,
} from '../types/seating';

export type { LockedChair, PlannedChair, UnseatedCode, UnseatedGuest } from './autoSeatTypes';
export type { SeatGrouping } from './autoSeatEngine';
export type { SeatPattern } from './seatRelation';
export type AutoSeatMode = 'fill_empty_seats' | 'reseat_everyone';

/** The server refuses a batch of more than this many items (GroupSeatingLimits.MaxBatchSeats). */
export const AUTO_SEAT_MAX_BATCH = 500;

export interface AutoSeatInput {
  mode: AutoSeatMode;
  /** The chart's `sourceVersion` at the moment the tables and candidates were read. */
  sourceVersion: number;
  tables: SeatingTable[];
  candidates: SeatingCandidate[];
  bonds: SeatBond[];
  /** Roster charts only: also seat guests who have not said whether they are going. */
  includeUnconfirmed?: boolean;
  /**
   * The randomness. Required, not defaulted: a caller must decide what "the same
   * seating again" means. Same seed + same input = the same plan, whatever order
   * the input arrives in.
   */
  seed: number;
  /** Default 'by_relationship'. */
  grouping?: SeatGrouping;
  /** How the two chairs of a pair sit. Default 'side_by_side'. */
  pattern?: SeatPattern;
  /** Household and relationship group per invitee id (lib/seatingGroups.ts). None = nobody is grouped. */
  groups?: ReadonlyMap<string, GuestGroups>;
  /** Guests the host pinned to a seat. Each lock is the guest's WHOLE party. */
  locks?: LockedChair[];
}

export interface AutoSeatPlan {
  mode: AutoSeatMode;
  sourceVersion: number;
  seed: number;
  grouping: SeatGrouping;
  pattern: SeatPattern;
  /** People pinned by the host and honoured. */
  lockedPeople: number;
  /** Locks that no longer apply (the seat is taken, the guest is gone) and were ignored. */
  droppedLocks: number;
  placements: PlannedChair[];
  /** Chairs emptied first (re-seat mode). Anyone emptied out is re-placed by `placements` or listed in `unseated`. */
  unassign: Array<{ tableId: string; tableLabel: string; seatIndex: number; name: string }>;
  unseated: UnseatedGuest[];
  warnings: string[];
  /** Roster guests left alone because they have not said they are going. */
  notConsidered: number;
  /** People this plan looked at and left exactly where they are. */
  keptInPlace: number;
  /** More changes than one batch may carry; the plan cannot be applied as it stands. */
  overLimit: boolean;
}

// ---- keys ----

const cmp = (a: string, b: string) => (a < b ? -1 : a > b ? 1 : 0);
const displayOf = (candidate: SeatingCandidate) => candidate.displayName.trim() || 'Guest';
const candidateKey = (candidate: SeatingCandidate) =>
  candidate.inviteeId ? `i:${candidate.inviteeId}` : `n:${normalizeName(candidate.displayName)}`;

/** Whose chair an assignment is, in {@link candidateKey}'s vocabulary; null for a blocked chair. */
function ownerKey(assignment: SeatAssignment): string | null {
  if (assignment.seatKind === 'blocked') return null;
  if (assignment.seatKind === 'guest') {
    return assignment.accompaniesInviteeId
      ? `i:${assignment.accompaniesInviteeId}`
      : `n:${normalizeName(assignment.guestLabel || assignment.displayName)}`;
  }
  return assignment.inviteeId ? `i:${assignment.inviteeId}` : null;
}

/** Whose chair a lock is, in the same vocabulary as {@link candidateKey}. */
export function lockKeyOf(chair: LockedChair): string | null {
  if (chair.seatKind === 'guest') {
    return chair.accompaniesInviteeId
      ? `i:${chair.accompaniesInviteeId}`
      : `n:${normalizeName(chair.guestLabel)}`;
  }
  return chair.inviteeId ? `i:${chair.inviteeId}` : null;
}

const isCompanion = (assignment: SeatAssignment) =>
  assignment.seatKind === 'guest' && Boolean(assignment.accompaniesInviteeId);

function tableOrder(tables: SeatingTable[]): SeatingTable[] {
  return [...tables].sort(
    (a, b) => a.displayOrder - b.displayOrder || cmp(a.label, b.label) || cmp(a.id, b.id),
  );
}

// ---- parties and units ----

function indexBonds(bonds: SeatBond[]): BondIndex {
  const index: BondIndex = {
    mustTogether: new Map(),
    softTogether: new Map(),
    mustApart: new Map(),
    softApart: new Map(),
  };
  const add = (map: Map<string, Set<string>>, x: string, y: string) => {
    if (!map.has(x)) map.set(x, new Set());
    map.get(x)!.add(y);
  };
  for (const bond of bonds) {
    const map =
      bond.seating === 'together'
        ? bond.strength === 'must' ? index.mustTogether : index.softTogether
        : bond.strength === 'must' ? index.mustApart : index.softApart;
    add(map, bond.a, bond.b);
    add(map, bond.b, bond.a);
  }
  return index;
}

const partnersOf = (map: Map<string, Set<string>>, id: string) => map.get(id) ?? new Set<string>();

// ---- the plan ----

export function planAutoSeat(input: AutoSeatInput): AutoSeatPlan {
  const reseat = input.mode === 'reseat_everyone';
  const tables = tableOrder(input.tables);
  const tableById = new Map(tables.map((table) => [table.id, table]));
  const bonds = indexBonds(input.bonds);
  const unseated: UnseatedGuest[] = [];
  const warnings: string[] = [];

  // -- who is being seated --
  const wanted: SeatingCandidate[] = [];
  let notConsidered = 0;
  const hasAnyChair = new Set<string>();
  for (const table of tables) {
    for (const assignment of seatMapFor(table).values()) {
      const key = ownerKey(assignment);
      if (key) hasAnyChair.add(key);
    }
  }
  for (const candidate of input.candidates) {
    const key = candidateKey(candidate);
    if (candidate.claimStatus === 'release_pending') {
      // Part-way out of the dinner: not worth a chair the host would take back.
      if (!hasAnyChair.has(key)) {
        unseated.push({
          name: displayOf(candidate),
          inviteeId: candidate.inviteeId ?? null,
          seats: Math.max(candidate.seatsNeeded, 1),
          code: 'giving_back_spot',
          reason: 'Is giving their dinner spot back, so no chair was proposed.',
        });
      }
      continue;
    }
    const attending = candidate.claimStatus
      ? true
      : String(candidate.rsvpStatus ?? '').trim().toLowerCase() === 'going';
    if (attending || input.includeUnconfirmed) wanted.push(candidate);
    else if (!hasAnyChair.has(key)) notConsidered += 1;
  }
  // Canonical order for the pre-shuffle only — by id, never by name.
  wanted.sort((a, b) => cmp(candidateKey(a), candidateKey(b)));
  const wantedKeys = new Set(wanted.map(candidateKey));

  // -- which existing chairs stay --
  interface Held { table: SeatingTable; seatIndex: number; assignment: SeatAssignment }
  const retained: Held[] = [];
  const cleared: Held[] = [];
  for (const table of tables) {
    for (const [seatIndex, assignment] of [...seatMapFor(table).entries()].sort((a, b) => a[0] - b[0])) {
      const key = ownerKey(assignment);
      const held = { table, seatIndex, assignment };
      if (reseat && key && wantedKeys.has(key)) cleared.push(held);
      else retained.push(held);
    }
  }
  const taken = new Map<string, Set<number>>(tables.map((table) => [table.id, new Set<number>()]));
  const peopleAt = new Map<string, Set<string>>(tables.map((table) => [table.id, new Set<string>()]));
  const tableOf = new Map<string, string>();
  const chairsOf = new Map<string, Held[]>();
  for (const held of retained) {
    taken.get(held.table.id)!.add(held.seatIndex);
    const { assignment } = held;
    if (assignment.seatKind === 'invitee' && assignment.inviteeId) {
      tableOf.set(assignment.inviteeId, held.table.id);
      peopleAt.get(held.table.id)!.add(assignment.inviteeId);
    }
    const key = ownerKey(assignment);
    if (key) chairsOf.set(key, [...(chairsOf.get(key) ?? []), held]);
  }

  // -- locks: guests the host pinned to a seat --
  // A lock is a whole party at exact chairs. It is honoured like a chair the host
  // placed by hand — nothing is planned around it that the hard rules forbid — but
  // it is WRITTEN by this plan, since the room does not hold it yet. A lock that
  // cannot hold (the chair is taken, the guest is gone, the party size changed) is
  // dropped and counted, never bent.
  const groups = input.groups ?? new Map<string, GuestGroups>();
  const previousByKey = (key: string) => {
    const own = cleared.find((held) => ownerKey(held.assignment) === key && !isCompanion(held.assignment));
    return own ? { tableLabel: own.table.label, seatIndex: own.seatIndex } : null;
  };
  const labelsFor = (inviteeId: string | null) => groupLabels(inviteeId ? groups.get(inviteeId) : undefined);
  const lockedPlacements: PlannedChair[] = [];
  const lockedOwners = new Set<string>();
  let droppedLocks = 0;
  const lockedByOwner = new Map<string, LockedChair[]>();
  for (const chair of input.locks ?? []) {
    const key = lockKeyOf(chair);
    if (key) lockedByOwner.set(key, [...(lockedByOwner.get(key) ?? []), chair]);
    else droppedLocks += 1;
  }
  const wantedByKey = new Map(wanted.map((candidate) => [candidateKey(candidate), candidate]));
  for (const [key, chairs] of [...lockedByOwner].sort((a, b) => cmp(a[0], b[0]))) {
    const candidate = wantedByKey.get(key);
    const need = candidate ? (candidate.inviteeId ? Math.max(candidate.seatsNeeded, 1) : 1) : -1;
    const seatIds = chairs.map((chair) => `${chair.tableId}:${chair.seatIndex}`);
    const holds =
      candidate !== undefined &&
      !chairsOf.has(key) &&
      chairs.length === need &&
      chairs.filter((chair) => !chair.companion).length === 1 &&
      new Set(seatIds).size === seatIds.length &&
      chairs.every((chair) => {
        const table = tableById.get(chair.tableId);
        return Boolean(table) && chair.seatIndex >= 0 && chair.seatIndex < table!.capacity
          && !taken.get(chair.tableId)!.has(chair.seatIndex);
      });
    if (!holds) {
      droppedLocks += 1;
      continue;
    }
    lockedOwners.add(key);
    for (const chair of chairs) {
      const table = tableById.get(chair.tableId)!;
      taken.get(table.id)!.add(chair.seatIndex);
      if (chair.seatKind === 'invitee' && chair.inviteeId) {
        tableOf.set(chair.inviteeId, table.id);
        peopleAt.get(table.id)!.add(chair.inviteeId);
      }
      lockedPlacements.push({
        ...chair,
        tableLabel: table.label,
        from: chair.companion ? null : previousByKey(key),
        ...labelsFor(chair.accompaniesInviteeId ?? chair.inviteeId),
        locked: true,
      });
    }
  }

  // -- parties: the chairs each wanted guest still needs --
  const parties: Party[] = [];
  let keptInPlace = 0;
  for (const candidate of wanted) {
    const key = candidateKey(candidate);
    if (lockedOwners.has(key)) continue;
    const held = chairsOf.get(key) ?? [];
    const ownSeated = held.some((entry) => !isCompanion(entry.assignment));
    const placedCompanions = held.filter((entry) => isCompanion(entry.assignment)).length;
    const name = displayOf(candidate);
    const chairs: ChairSpec[] = [];
    if (!ownSeated) {
      chairs.push(
        candidate.inviteeId
          ? { seatKind: 'invitee', inviteeId: candidate.inviteeId, accompaniesInviteeId: null, guestLabel: '', name, companion: false }
          : { seatKind: 'guest', inviteeId: null, accompaniesInviteeId: null, guestLabel: name, name, companion: false },
      );
    }
    const wantCompanions = Math.max(candidate.seatsNeeded - 1, 0);
    let unsupportedExtra = 0;
    if (candidate.inviteeId) {
      for (let i = placedCompanions; i < wantCompanions; i += 1) {
        chairs.push({
          seatKind: 'guest',
          inviteeId: null,
          accompaniesInviteeId: candidate.inviteeId,
          guestLabel: `${name}'s +1`,
          name: `${name}'s +1`,
          companion: true,
        });
      }
    } else {
      // A companion chair must point at a claimer's invitee id, and a claim the
      // host typed in has none. The picker does not offer these either.
      unsupportedExtra = Math.max(wantCompanions - placedCompanions, 0);
    }
    if (chairs.length === 0 && unsupportedExtra === 0) {
      keptInPlace += 1;
      continue;
    }
    const ownChair = held.find((entry) => !isCompanion(entry.assignment)) ?? held[0];
    parties.push({ candidate, key, chairs, pin: ownChair ? ownChair.table.id : null, unsupportedExtra });
  }

  // -- units: parties joined by a 'must' keep-together share a table --
  const partyById = new Map<string, Party>();
  for (const party of parties) if (party.candidate.inviteeId) partyById.set(party.candidate.inviteeId, party);
  const parent = new Map<Party, Party>(parties.map((party) => [party, party]));
  const find = (party: Party): Party => {
    let root = party;
    while (parent.get(root) !== root) root = parent.get(root)!;
    return root;
  };
  for (const party of parties) {
    const id = party.candidate.inviteeId;
    if (!id) continue;
    for (const other of partnersOf(bonds.mustTogether, id)) {
      const mate = partyById.get(other);
      if (mate) parent.set(find(mate), find(party));
    }
  }
  const unitMap = new Map<Party, Unit>();
  for (const party of parties) {
    const root = find(party);
    if (!unitMap.has(root)) unitMap.set(root, { parties: [], chairs: 0, pins: new Set(), conflict: null, key: '' });
    const unit = unitMap.get(root)!;
    unit.parties.push(party);
    unit.chairs += party.chairs.length;
    if (party.pin) unit.pins.add(party.pin);
  }
  const units = [...unitMap.values()];
  for (const unit of units) {
    unit.key = unit.parties.map((party) => party.key).sort()[0];
    const ids = unit.parties.map((party) => party.candidate.inviteeId).filter(Boolean) as string[];
    for (const id of ids) {
      // A keep-together partner who already has a chair fixes the table.
      for (const other of partnersOf(bonds.mustTogether, id)) {
        const at = tableOf.get(other);
        if (at) unit.pins.add(at);
      }
      // Two 'together' and 'apart' rules that contradict through a third guest.
      for (const other of partnersOf(bonds.mustApart, id)) {
        if (ids.includes(other)) unit.conflict = 'Their keep-together and keep-apart rules contradict each other.';
      }
    }
    if (unit.pins.size > 1) {
      unit.conflict = 'Guests they must sit with are already at different tables.';
    }
  }

  // -- placement: which table (engine), then which seat (seat pass) --
  units.sort((x, y) => cmp(x.key, y.key));
  const names = new Map(input.candidates.filter((c) => c.inviteeId).map((c) => [c.inviteeId!, displayOf(c)]));
  const placed = placeUnits({
    tables,
    units,
    bonds,
    baseline: { taken, peopleAt, tableOf },
    groups,
    grouping: input.grouping ?? 'by_relationship',
    pattern: input.pattern ?? 'side_by_side',
    seed: input.seed,
    names,
    previousChair: (party) => previousByKey(party.key),
  });
  const order = new Map(tables.map((table, index) => [table.id, index]));
  const placements = [...placed.placements, ...lockedPlacements].sort(
    (x, y) => order.get(x.tableId)! - order.get(y.tableId)! || x.seatIndex - y.seatIndex,
  );
  unseated.push(...placed.unseated);
  warnings.push(...placed.warnings);

  for (const party of parties) {
    if (party.unsupportedExtra > 0) {
      unseated.push({
        name: displayOf(party.candidate),
        inviteeId: party.candidate.inviteeId ?? null,
        seats: party.unsupportedExtra,
        code: 'extra_seats_unsupported',
        reason: `Hand-entered claim for a party of ${party.candidate.seatsNeeded}: their other ${party.unsupportedExtra} seat${party.unsupportedExtra === 1 ? '' : 's'} need adding by hand.`,
      });
    }
  }

  // -- re-seat: diff against the chairs as they are, so unchanged people are not touched --
  const unassign: AutoSeatPlan['unassign'] = [];
  let finalPlacements = placements;
  if (reseat) {
    const kept = new Set<Held>();
    finalPlacements = placements.filter((chair) => {
      const same = cleared.find(
        (held) =>
          held.table.id === chair.tableId &&
          held.seatIndex === chair.seatIndex &&
          sameOccupant(held.assignment, chair),
      );
      if (same) kept.add(same);
      return !same;
    });
    // People, not chairs: a party of three left where it was is one guest kept in place.
    keptInPlace += new Set([...kept].map((held) => ownerKey(held.assignment))).size;
    for (const held of cleared) {
      if (kept.has(held)) continue;
      unassign.push({
        tableId: held.table.id,
        tableLabel: held.table.label,
        seatIndex: held.seatIndex,
        name: held.assignment.displayName || held.assignment.guestLabel || 'Guest',
      });
    }
  }

  return {
    mode: input.mode,
    sourceVersion: input.sourceVersion,
    seed: input.seed,
    grouping: input.grouping ?? 'by_relationship',
    pattern: input.pattern ?? 'side_by_side',
    lockedPeople: lockedOwners.size,
    droppedLocks,
    placements: finalPlacements,
    unassign,
    unseated,
    warnings,
    notConsidered,
    keptInPlace,
    overLimit: finalPlacements.length + unassign.length > AUTO_SEAT_MAX_BATCH,
  };
}

/** Is this chair already held by exactly the occupant the plan wants in it? */
function sameOccupant(current: SeatAssignment, chair: PlannedChair): boolean {
  if (chair.seatKind === 'invitee') {
    return current.seatKind === 'invitee' && current.inviteeId === chair.inviteeId;
  }
  if (current.seatKind !== 'guest') return false;
  if (chair.accompaniesInviteeId) {
    // Companion chairs of one claimer are interchangeable.
    return current.accompaniesInviteeId === chair.accompaniesInviteeId;
  }
  return !current.accompaniesInviteeId
    && normalizeName(current.guestLabel || current.displayName) === normalizeName(chair.guestLabel);
}

// ---- the request ----

/**
 * The plan as the one request the server already has for a whole-room change.
 * Assign items carry no `fromAssignmentId`: a chair being left is in `unassign`,
 * which the server applies first, so nothing is ever moved by inference.
 */
export function toSeatBatch(plan: AutoSeatPlan): SeatBatchRequest {
  return {
    unassign: plan.unassign.map(({ tableId, seatIndex }) => ({ tableId, seatIndex })),
    assign: plan.placements.map((chair) => ({
      tableId: chair.tableId,
      seatIndex: chair.seatIndex,
      seatKind: chair.seatKind,
      ...(chair.seatKind === 'invitee'
        ? { inviteeId: chair.inviteeId }
        : {
            accompaniesInviteeId: chair.accompaniesInviteeId,
            guestLabel: chair.guestLabel,
          }),
    })),
  };
}

/** Nothing would change. The dialog says so instead of offering an empty Apply. */
export const isNoOp = (plan: AutoSeatPlan) => plan.placements.length === 0 && plan.unassign.length === 0;

/** The proposal, grouped by table in room order — what the preview lists. */
export function placementsByTable(plan: AutoSeatPlan, tables: SeatingTable[]) {
  return tableOrder(tables)
    .map((table) => ({
      table,
      chairs: plan.placements
        .filter((chair) => chair.tableId === table.id)
        .sort((a, b) => a.seatIndex - b.seatIndex),
    }))
    .filter((entry) => entry.chairs.length > 0);
}
